import type { Tenant } from "@/types/index"

/**
 * Leitura da rede (migration 293, tarefa wdwxne02aa).
 *
 * Quem está na lista `credit_v2_leitores_da_rede` lê todas as unidades e grava
 * só onde tem vínculo ativo. Hoje é só o usuário de teste da rotina das 6h, que
 * precisa conferir no navegador, em produção, o que a fila publicou em
 * unidades com SOMA, Financeiro e casos reais (decisão do PM em 20/10/2026).
 *
 * A função do banco `unidades_da_leitura()` devolve essas unidades extras, sem
 * as colunas de chave, e `[]` para todo o resto. Esta regra junta a resposta à
 * lista de vínculos e marca as extras com `somente_leitura`, para o seletor do
 * topo dizer isso e as telas não oferecerem gravação nelas.
 *
 * ⚠️ Quem decide o que cada pessoa lê e grava é a RLS no servidor. Esta marca
 * é aviso de tela: se ela faltar, a gravação numa unidade lida continua
 * recusada lá.
 *
 * ⚠️ FALHA FECHADA. Resposta que não seja lista (função ainda não aplicada
 * neste banco, erro de rede) vira nenhuma unidade extra, e a pessoa fica com os
 * vínculos de sempre.
 */
export function juntarUnidadesLidas(vinculadas: Tenant[], resposta: unknown): Tenant[] {
  if (!Array.isArray(resposta)) return vinculadas
  const ja = new Set(vinculadas.map((t) => t.id))
  const lidas = resposta
    .filter(
      (t): t is Tenant =>
        typeof t === "object" &&
        t !== null &&
        typeof (t as Tenant).id === "string" &&
        typeof (t as Tenant).name === "string",
    )
    .filter((t) => !ja.has(t.id) && !t.deactivated_at)
    .map((t) => ({ ...t, somente_leitura: true }))
    .sort((a, b) => a.name.localeCompare(b.name, "pt-BR"))
  return lidas.length > 0 ? [...vinculadas, ...lidas] : vinculadas
}

/** Se a unidade do escopo é uma das lidas, onde a tela não deve oferecer gravação. */
export function unidadeSoDeLeitura(
  unidades: Tenant[] | null | undefined,
  tenantId: string | null | undefined,
): boolean {
  if (!tenantId || !unidades) return false
  return unidades.some((t) => t.id === tenantId && t.somente_leitura === true)
}

/** Nome da unidade no seletor do topo. */
export function nomeNoSeletor(t: Tenant): string {
  return t.somente_leitura ? `${t.name} (só leitura)` : t.name
}

/**
 * O endereço (slug) de uma unidade lida, tirado da resposta de
 * `unidades_da_leitura()`. A tabela das unidades não abre ao leitor (ela guarda
 * a chave da Devolutiva ao Meta), então quem precisa do slug de uma unidade
 * lida pede a lista sem as chaves. `null` quando a unidade não está na resposta
 * ou a resposta não é lista.
 */
export function slugDaUnidadeLida(resposta: unknown, tenantId: string): string | null {
  if (!Array.isArray(resposta)) return null
  const achada = resposta.find(
    (t): t is { id: string; slug?: unknown } =>
      typeof t === "object" && t !== null && (t as { id?: unknown }).id === tenantId,
  )
  return achada && typeof achada.slug === "string" && achada.slug ? achada.slug : null
}

/** Texto das telas que não abrem numa unidade lida. */
export const AVISO_UNIDADE_SO_DE_LEITURA =
  "Esta unidade está aberta só para leitura. Para mexer nas configurações, escolha uma unidade sua no seletor do topo."
